import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Image as ImageIcon, SlidersHorizontal, UserPlus, RefreshCw, Search, ArrowUpDown } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  loadVariableRateBonds,
  clearVariableRateBonds,
  putClassification,
} from '../lib/variableRateBonds';
import VrClassificationBadge from '../components/VrClassificationBadge';
import VariableRateBondPanel from '../components/VariableRateBondPanel';
import AccountGroupManager from '../components/AccountGroupManager';
import FirmBrandingPanel from '../components/FirmBrandingPanel';

/**
 * Manual data-entry section. Exposes:
 *   - "Add / Remove / Edit an Account or Group" - Postgres CRUD over report
 *     groups and accounts (client_groups / clients / mappings).
 *   - "Update Variable Rate Bonds" - list every variable-rate bond held across
 *     the firm's accounts, classify it (VR / not variable / unclassified), and
 *     maintain its effective-dated rate timeline (add/edit/delete points). The
 *     merged timeline flows into all FI reports.
 *   - "Report Branding" - the firm's custom text or logo, printed in the top
 *     left of page 1 of every report.
 */
const ManualPage = () => {
  const { darkMode } = useTheme();
  const { fetchWithAuth } = useAuth();
  const [view, setView] = useState(null); // null | 'vrm' | 'manage' | 'branding'

  const card = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';

  // h-full + overflow-y-auto because nothing above this scrolls: customScrollbar.css
  // pins html/body to overflow:hidden and MainLayout wraps every route in an
  // overflow-hidden flex column, so a page that doesn't opt in is simply clipped.
  // Mirrors ReportsPage.
  return (
    <div className={`h-full overflow-y-auto p-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
      <h3 className="text-2xl font-bold mb-1">Manual</h3>
      <p className={`mb-6 ${muted}`}>Manually-maintained inputs that the data feed can't supply.</p>

      {!view && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
          <button
            type="button"
            onClick={() => setView('manage')}
            className={`text-left p-5 rounded-xl border ${card} hover:shadow-md transition-shadow`}
          >
            <div className="flex items-center gap-3 mb-2">
              <UserPlus className="w-5 h-5" />
              <span className="font-semibold">Add / Remove / Edit an Account or Group</span>
            </div>
            <p className={`text-sm ${muted}`}>Create report groups (the @names), manage their accounts, and add or edit accounts.</p>
          </button>

          <button
            type="button"
            onClick={() => setView('vrm')}
            className={`text-left p-5 rounded-xl border ${card} hover:shadow-md transition-shadow`}
          >
            <div className="flex items-center gap-3 mb-2">
              <SlidersHorizontal className="w-5 h-5" />
              <span className="font-semibold">Update Variable Rate Bonds</span>
            </div>
            <p className={`text-sm ${muted}`}>
              Classify variable-rate bonds and maintain their rate timeline. Applied across all fixed-income reports.
            </p>
          </button>

          <button
            type="button"
            onClick={() => setView('branding')}
            className={`text-left p-5 rounded-xl border ${card} hover:shadow-md transition-shadow`}
          >
            <div className="flex items-center gap-3 mb-2">
              <ImageIcon className="w-5 h-5" />
              <span className="font-semibold">Report Branding</span>
            </div>
            <p className={`text-sm ${muted}`}>
              Set the custom text or logo printed in the top left of page 1 of every report.
            </p>
          </button>
        </div>
      )}

      {view === 'vrm' && (
        <VariableRateBonds onBack={() => setView(null)} fetchWithAuth={fetchWithAuth} darkMode={darkMode} />
      )}

      {view === 'manage' && (
        <AccountGroupManager onBack={() => setView(null)} fetchWithAuth={fetchWithAuth} darkMode={darkMode} />
      )}

      {view === 'branding' && (
        <FirmBrandingPanel onBack={() => setView(null)} fetchWithAuth={fetchWithAuth} darkMode={darkMode} />
      )}
    </div>
  );
};

const fmtNum = (n) =>
  n === null || n === undefined ? '-' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
const fmtRate = (n) => (n === null || n === undefined ? '-' : `${Number(n).toFixed(3)}%`);
const fmtWhen = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

const VariableRateBonds = ({ onBack, fetchWithAuth, darkMode }) => {
  const [bonds, setBonds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [query, setQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'market_value', direction: 'desc' });
  const [selected, setSelected] = useState(null); // the opened bond, or null
  const [savingCusip, setSavingCusip] = useState(null);

  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';

  // Read through the shared cache (warmed at login), so the view renders with
  // zero fetch. Refresh + every mutation pass { force: true } to bypass it.
  const load = useCallback(async ({ force = false } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await loadVariableRateBonds({ force, fetcher: fetchWithAuth });
      setBonds(data);
    } catch (e) {
      setError(e.message || 'Failed to load variable-rate bonds');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => { load(); }, [load]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return bonds;
    return bonds.filter(
      (b) =>
        (b.cusip || '').toLowerCase().includes(q) ||
        (b.description || '').toLowerCase().includes(q)
    );
  }, [bonds, query]);

  const sorted = useMemo(() => {
    const rows = [...filtered];
    const { key, direction } = sortConfig;
    rows.sort((a, b) => {
      if (key === 'cusip' || key === 'description' || key === 'maturity_date') {
        const av = (a[key] || '').toString().toLowerCase();
        const bv = (b[key] || '').toString().toLowerCase();
        return direction === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      const av = a[key] ?? 0;
      const bv = b[key] ?? 0;
      return direction === 'asc' ? av - bv : bv - av;
    });
    return rows;
  }, [filtered, sortConfig]);

  // Apply a classification (true|false|null) for a CUSIP, then drop the cache
  // and refetch so the list reflects the server truth (no client echo).
  const setClassification = async (bond, isVariable) => {
    setSavingCusip(bond.cusip);
    setError(null);
    setNotice(null);
    try {
      await putClassification(bond.cusip, { is_variable: isVariable }, fetchWithAuth);
      clearVariableRateBonds();
      await load({ force: true });
      setNotice(`Classification updated for ${bond.cusip}`);
    } catch (e) {
      setError(e.message || 'Failed to update classification');
    } finally {
      setSavingCusip(null);
    }
  };

  // A per-bond mutation happened inside the panel; drop the cache and refetch
  // the list so current_rate / last_entry / staleness reflect the change.
  const onPanelMutated = useCallback(() => {
    clearVariableRateBonds();
    load({ force: true });
  }, [load]);

  if (selected) {
    return (
      <div>
        <div className="mb-4">
          <button onClick={() => setSelected(null)} className={`text-sm ${muted} hover:underline`}>
            &larr; Variable Rate Bonds
          </button>
          <h4 className="text-xl font-semibold mt-1">
            {selected.cusip} - {selected.description || '-'}
          </h4>
          <p className={`text-sm ${muted}`}>
            Maturity {selected.maturity_date || '-'} &middot; Par {fmtNum(selected.par_value)} &middot; Current rate {fmtRate(selected.current_rate)}
          </p>
        </div>
        <VariableRateBondPanel
          bond={selected}
          fetchWithAuth={fetchWithAuth}
          onMutated={onPanelMutated}
          darkMode={darkMode}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <button onClick={onBack} className={`text-sm ${muted} hover:underline`}>&larr; Manual</button>
          <h4 className="text-xl font-semibold mt-1">Update Variable Rate Bonds</h4>
          <p className={`text-sm ${muted}`}>Classify each bond and maintain its effective-dated rate timeline.</p>
        </div>
        <button onClick={() => load({ force: true })} className={`flex items-center gap-2 text-sm ${muted} hover:underline`}>
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {error && <div className="mb-3 px-3 py-2 rounded bg-red-100 text-red-800 text-sm">{error}</div>}
      {notice && <div className="mb-3 px-3 py-2 rounded bg-green-100 text-green-800 text-sm">{notice}</div>}

      <div className="mb-3 relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search CUSIP or description..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className={`w-full pl-9 pr-3 py-2 rounded border text-sm ${
            darkMode ? 'bg-gray-900 border-gray-600' : 'bg-white border-gray-300'
          }`}
        />
      </div>

      {loading ? (
        <p className={muted}>Loading...</p>
      ) : sorted.length === 0 ? (
        <p className={muted}>No variable-rate bonds held across your accounts.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="cursor-pointer" onClick={() => handleSort('cusip')}>
                <span className="inline-flex items-center gap-1">CUSIP <ArrowUpDown className="w-3 h-3" /></span>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('description')}>Description</TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('maturity_date')}>Maturity</TableHead>
              <TableHead className="text-right cursor-pointer" onClick={() => handleSort('par_value')}>Par</TableHead>
              <TableHead className="text-right cursor-pointer" onClick={() => handleSort('market_value')}>MV</TableHead>
              <TableHead className="text-right cursor-pointer" onClick={() => handleSort('current_rate')}>Rate</TableHead>
              <TableHead>Classification</TableHead>
              <TableHead>Last entry</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((b) => (
              <TableRow
                key={b.security_id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => setSelected(b)}
              >
                <TableCell className="font-mono" data-testid="vrb-cusip">{b.cusip || b.security_id}</TableCell>
                <TableCell className="max-w-[220px] truncate">{b.description || '-'}</TableCell>
                <TableCell>{b.maturity_date || '-'}</TableCell>
                <TableCell className="text-right font-mono">{fmtNum(b.par_value)}</TableCell>
                <TableCell className="text-right font-mono">{fmtNum(b.market_value)}</TableCell>
                <TableCell className="text-right font-mono">{fmtRate(b.current_rate)}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-2">
                    <VrClassificationBadge
                      isVariable={b.is_variable ?? null}
                      onSet={(v) => setClassification(b, v)}
                      disabled={savingCusip === b.cusip}
                    />
                    {b.is_stale && (
                      <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                        Stale &gt;35d
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-xs">
                  {b.last_entry_rate !== null && b.last_entry_rate !== undefined ? (
                    <>
                      <div className="font-mono">{fmtRate(b.last_entry_rate)} &middot; {b.last_entry_date}</div>
                      <div className={muted}>
                        {b.last_entry_by_name || 'PM'} &middot; {fmtWhen(b.last_entry_at)}
                      </div>
                    </>
                  ) : (
                    <span className={muted}>-</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default ManualPage;
